// src/service/ratesService.js
import { doc, onSnapshot, collection } from "firebase/firestore";
import { db } from "./firebase";

const OZ_TO_GRAM = 31.103;
const USD_TO_INR_FALLBACK = 86.8;

// Supported Indian states for regional rate calculations
export const SUPPORTED_STATES = [
  { stateName: "India", stateCode: "IN" },
  { stateName: "Andhra Pradesh (Vijayawada/Vizag)", stateCode: "AP" },
  { stateName: "Arunachal Pradesh (Itanagar)", stateCode: "AR" },
  { stateName: "Assam (Guwahati)", stateCode: "AS" },
  { stateName: "Bihar (Patna)", stateCode: "BR" },
  { stateName: "Chhattisgarh (Raipur)", stateCode: "CG" },
  { stateName: "Goa (Panaji)", stateCode: "GA" },
  { stateName: "Gujarat (Ahmedabad/Surat)", stateCode: "GJ" },
  { stateName: "Haryana (Gurugram/Faridabad)", stateCode: "HR" },
  { stateName: "Himachal Pradesh (Shimla)", stateCode: "HP" },
  { stateName: "Jharkhand (Ranchi/Jamshedpur)", stateCode: "JH" },
  { stateName: "Karnataka (Bengaluru)", stateCode: "KA" },
  { stateName: "Kerala (Kochi/Thrissur)", stateCode: "KL" },
  { stateName: "Madhya Pradesh (Indore/Bhopal)", stateCode: "MP" },
  { stateName: "Maharashtra (Mumbai)", stateCode: "MH" },
  { stateName: "Manipur (Imphal)", stateCode: "MN" },
  { stateName: "Meghalaya (Shillong)", stateCode: "ML" },
  { stateName: "Mizoram (Aizawl)", stateCode: "MZ" },
  { stateName: "Nagaland (Kohima/Dimapur)", stateCode: "NL" },
  { stateName: "Odisha (Bhubaneswar)", stateCode: "OD" },
  { stateName: "Punjab (Amritsar/Ludhiana)", stateCode: "PB" },
  { stateName: "Rajasthan (Jaipur)", stateCode: "RJ" },
  { stateName: "Sikkim (Gangtok)", stateCode: "SK" },
  { stateName: "Tamil Nadu (Chennai)", stateCode: "TN" },
  { stateName: "Telangana (Hyderabad)", stateCode: "TS" },
  { stateName: "Tripura (Agartala)", stateCode: "TR" },
  { stateName: "Uttar Pradesh (Lucknow/Kanpur)", stateCode: "UP" },
  { stateName: "Uttarakhand (Dehradun)", stateCode: "UK" },
  { stateName: "West Bengal (Kolkata)", stateCode: "WB" },
  // Union Territories
  { stateName: "Andaman and Nicobar Islands (Port Blair)", stateCode: "AN" },
  { stateName: "Chandigarh", stateCode: "CH" },
  { stateName: "Dadra & Nagar Haveli and Daman & Diu", stateCode: "DD" },
  { stateName: "Delhi (NCR)", stateCode: "DL" },
  { stateName: "Jammu and Kashmir (Srinagar/Jammu)", stateCode: "JK" },
  { stateName: "Ladakh (Leh)", stateCode: "LA" },
  { stateName: "Lakshadweep (Kavaratti)", stateCode: "LD" },
  { stateName: "Puducherry", stateCode: "PY" },
];

class RatesService {
  constructor() {
    this.premiums = { gold: 0, silver: 0, platinum: 0 };
    this.stateConstraints = {};
    this.rawSpot = {
      goldUsd: 2980.5,
      silverUsd: 33.8,
      platinumUsd: 980.0,
      usdToInr: 86.85,
    };
    this.currentRates = null;
    this.previousRates = null;
    this.listeners = new Set();
    this.selectedState = "IN";
    this.isInitialized = false;
    this.timer = null;
    this.apiTimer = null;

    this.initFirebaseListeners();
    this.fetchSpotPrices();
  }

  initFirebaseListeners() {
    try {
      // 1. Listen to SuperAdmin metal premiums from systemConfig/metalPremiums
      const premiumsRef = doc(db, "systemConfig", "metalPremiums");
      onSnapshot(premiumsRef, (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          const p = data.premiums || {};
          this.premiums = {
            gold: Number(p.gold ?? 0),
            silver: Number(p.silver ?? 0),
            platinum: Number(p.platinum ?? 0),
          };
          this.computeRates();
        }
      }, (err) => {
        console.warn("[RatesService] Premiums listener error:", err.message);
      });

      // 2. Listen to SuperAdmin state constraints from rateConstraints collection
      const constraintsRef = collection(db, "rateConstraints");
      onSnapshot(constraintsRef, (snap) => {
        const map = {};
        snap.forEach((d) => {
          map[d.id.toUpperCase()] = d.data();
        });
        this.stateConstraints = map;
        this.computeRates();
      }, (err) => {
        console.warn("[RatesService] Constraints listener error:", err.message);
      });
    } catch (e) {
      console.warn("[RatesService] Firebase listener initialization:", e);
    }
  }

  async fetchSpotPrices() {
    try {
      const [goldRes, silverRes, platRes, fxRes] = await Promise.all([
        fetch("https://api.gold-api.com/price/XAU").catch(() => null),
        fetch("https://api.gold-api.com/price/XAG").catch(() => null),
        fetch("https://api.gold-api.com/price/XPT").catch(() => null),
        fetch("https://open.er-api.com/v6/latest/USD").catch(() => null),
      ]);

      const goldJson = goldRes && goldRes.ok ? await goldRes.json() : null;
      const silverJson = silverRes && silverRes.ok ? await silverRes.json() : null;
      const platJson = platRes && platRes.ok ? await platRes.json() : null;
      const fxJson = fxRes && fxRes.ok ? await fxRes.json() : null;

      if (goldJson?.price) this.rawSpot.goldUsd = Number(goldJson.price);
      if (silverJson?.price) this.rawSpot.silverUsd = Number(silverJson.price);
      if (platJson?.price) this.rawSpot.platinumUsd = Number(platJson.price);
      if (fxJson?.rates?.INR) this.rawSpot.usdToInr = Number(fxJson.rates.INR);

      this.computeRates();
    } catch (err) {
      console.warn("[RatesService] Spot fetch error:", err);
    }

    // Refresh external market API periodically (every 15s)
    if (!this.apiTimer) {
      this.apiTimer = setInterval(() => this.fetchSpotPrices(), 15000);
    }

    // 1-second refresh cycle for live moving ticker
    if (!this.timer) {
      this.start1SecondTicker();
    }
  }

  start1SecondTicker() {
    this.timer = setInterval(() => {
      // Apply micro sub-pip jitter to simulate live orderbook tick when markets are moving
      const jitter = (Math.random() - 0.5) * 0.02; // ±0.01%
      this.computeRates(jitter);
    }, 1000);
  }

  computeRates(jitterPercent = 0) {
    const usdToInr = this.rawSpot.usdToInr || USD_TO_INR_FALLBACK;
    const goldSpot = this.rawSpot.goldUsd * (1 + jitterPercent / 100);
    const silverSpot = this.rawSpot.silverUsd * (1 + jitterPercent / 100);
    const platSpot = this.rawSpot.platinumUsd * (1 + jitterPercent / 100);

    // Step 1: Base INR per gram
    const baseGoldPerGram = (goldSpot * usdToInr) / OZ_TO_GRAM;
    const baseSilverPerGram = (silverSpot * usdToInr) / OZ_TO_GRAM;
    const basePlatPerGram = (platSpot * usdToInr) / OZ_TO_GRAM;

    // Step 2: SuperAdmin Premium calculation
    const goldPremiumVal = baseGoldPerGram * (this.premiums.gold / 100);
    const silverPremiumVal = baseSilverPerGram * (this.premiums.silver / 100);
    const platPremiumVal = basePlatPerGram * (this.premiums.platinum / 100);

    let gold24kGram = baseGoldPerGram + goldPremiumVal;
    let silverGram = baseSilverPerGram + silverPremiumVal;
    let platGram = basePlatPerGram + platPremiumVal;

    // Step 3: State Constraint adjustment (if a specific state is selected)
    let stateMarkup = 0;
    let stateInfo = null;
    if (this.selectedState !== "IN" && this.stateConstraints[this.selectedState]) {
      const constraint = this.stateConstraints[this.selectedState];
      if (constraint.active !== false) {
        stateMarkup = Number(constraint.gold?.rtgsSale || 0);
        gold24kGram = gold24kGram * (1 + stateMarkup / 100);
        const silverMarkup = Number(constraint.silver?.rtgsSale || 0);
        silverGram = silverGram * (1 + silverMarkup / 100);
        stateInfo = constraint.stateName || this.selectedState;
      }
    }

    // Purity calculations
    const gold24k_10g = Math.round(gold24kGram * 10);
    const gold22k_10g = Math.round(gold24k_10g * (22 / 24)); // 916 Hallmark
    const gold18k_10g = Math.round(gold24k_10g * (18 / 24)); // 750 Diamond alloy
    const silver999_1kg = Math.round(silverGram * 1000);
    const silver925_10g = Math.round(silverGram * 10 * 0.925);
    const plat950_10g = Math.round(platGram * 10 * 0.95);

    // Compute delta against previous second's snapshot
    const prev = this.previousRates || {
      gold24k: gold24k_10g,
      gold22k: gold22k_10g,
      gold18k: gold18k_10g,
      silver999: silver999_1kg,
      platinum950: plat950_10g,
    };

    const calcChange = (curr, previous) => {
      const diff = curr - previous;
      const pct = previous ? ((diff / previous) * 100).toFixed(2) : "0.00";
      const isPos = diff >= 0;
      return {
        diff,
        pct: (isPos ? "+" : "") + pct + "%",
        positive: isPos,
        formatted: `${isPos ? "+" : ""}₹${Math.abs(diff).toLocaleString("en-IN")}`,
      };
    };

    const now = new Date();
    const timeFormatted = now.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });

    const newRates = {
      timestamp: now,
      timeFormatted,
      selectedState: this.selectedState,
      stateInfo,
      premiums: this.premiums,
      gold24k: {
        name: "GOLD 24K (999)",
        label: "Fine Gold 999",
        price: gold24k_10g,
        priceFormatted: `₹${gold24k_10g.toLocaleString("en-IN")}`,
        perGram: Math.round(gold24kGram),
        unit: "/ 10g",
        change: calcChange(gold24k_10g, prev.gold24k),
      },
      gold22k: {
        name: "GOLD 22K (916)",
        label: "Hallmark Jewellery 916",
        price: gold22k_10g,
        priceFormatted: `₹${gold22k_10g.toLocaleString("en-IN")}`,
        perGram: Math.round(gold22k_10g / 10),
        unit: "/ 10g",
        change: calcChange(gold22k_10g, prev.gold22k),
      },
      gold18k: {
        name: "GOLD 18K (750)",
        label: "Diamond Setting Standard",
        price: gold18k_10g,
        priceFormatted: `₹${gold18k_10g.toLocaleString("en-IN")}`,
        perGram: Math.round(gold18k_10g / 10),
        unit: "/ 10g",
        change: calcChange(gold18k_10g, prev.gold18k),
      },
      silver999: {
        name: "SILVER 999",
        label: "Fine Bullion & Castings",
        price: silver999_1kg,
        priceFormatted: `₹${silver999_1kg.toLocaleString("en-IN")}`,
        perGram: Math.round(silverGram),
        unit: "/ kg",
        change: calcChange(silver999_1kg, prev.silver999),
      },
      silver925: {
        name: "SILVER 925",
        label: "Sterling Silver Standard",
        price: silver925_10g,
        priceFormatted: `₹${silver925_10g.toLocaleString("en-IN")}`,
        perGram: Math.round(silver925_10g / 10),
        unit: "/ 10g",
        change: calcChange(silver925_10g, prev.silver925 || silver925_10g),
      },
      platinum950: {
        name: "PLATINUM 950",
        label: "Pt 950 Certified Alloy",
        price: plat950_10g,
        priceFormatted: `₹${plat950_10g.toLocaleString("en-IN")}`,
        perGram: Math.round(plat950_10g / 10),
        unit: "/ 10g",
        change: calcChange(plat950_10g, prev.platinum950),
      },
    };

    this.previousRates = {
      gold24k: gold24k_10g,
      gold22k: gold22k_10g,
      gold18k: gold18k_10g,
      silver999: silver999_1kg,
      silver925: silver925_10g,
      platinum950: plat950_10g,
    };

    this.currentRates = newRates;
    this.notify();
  }

  setState(stateCode) {
    this.selectedState = stateCode;
    this.computeRates();
  }

  subscribe(callback) {
    this.listeners.add(callback);
    if (this.currentRates) {
      callback(this.currentRates);
    }
    return () => this.listeners.delete(callback);
  }

  notify() {
    for (const cb of this.listeners) {
      try {
        cb(this.currentRates);
      } catch (err) {
        console.error("[RatesService] Listener error:", err);
      }
    }
  }

  getCurrentRates() {
    return this.currentRates;
  }
}

export const ratesService = new RatesService();
